import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BatchPackageMatch, BatchPackageState, BatchReturnStatusType } from 'returnCenter-api';
import useAppDispatch from 'hooks/useAppDispatch';
import {
    BatchPackageActionError,
    createReturnSlipForPackage,
    receiveBatchReturn,
    reopenBatch,
    unmarkPackageMissing,
} from 'store/reducers/batchReturnSlice';
import { getStatusTypeString } from 'utils/helpers';

export type BatchPackagePrompt =
    | { kind: 'choose'; matches: BatchPackageMatch[] }
    | { kind: 'receive'; match: BatchPackageMatch }
    | { kind: 'missing'; match: BatchPackageMatch }
    | { kind: 'reopen'; match: BatchPackageMatch };

const toastOptions = { autoClose: 3500, closeOnClick: true };

/**
 * A scanned package that belongs to a batch sent to us: open its return slip, creating it first when
 * needed. Receiving the batch, undoing a missing mark, or reopening the completed batch a missing
 * package turned up for, is asked for before anything is created.
 */
const useBatchPackageScan = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [prompt, setPrompt] = useState<BatchPackagePrompt | null>(null);
    const [busy, setBusy] = useState(false);

    const openReturnSlip = useCallback(
        (returnId: number, returnStatus?: number | null) => {
            const statusKey = getStatusTypeString(returnStatus ?? undefined);
            navigate(`/returns/${statusKey}/${returnId}`, {
                state: { origin: statusKey, returnStatus },
            });
        },
        [navigate],
    );

    const showError = useCallback(
        (error: BatchPackageActionError | undefined) => {
            const message =
                error?.code === 'inProgress'
                    ? t(
                          'batchPackage.inProgress',
                          'A return slip for this package is being created right now. Scan again in a moment.',
                      )
                    : t('batchPackage.createFailed', 'Could not create a return slip for the package.');
            toast.error(message, toastOptions);
        },
        [t],
    );

    const createSlip = useCallback(
        async (match: BatchPackageMatch) => {
            const result = await dispatch(
                createReturnSlipForPackage({
                    batchReturnId: match.batchReturnId!,
                    sourceReturnId: match.sourceReturnId!,
                }),
            );
            if (createReturnSlipForPackage.fulfilled.match(result)) {
                openReturnSlip(result.payload.returnId, result.payload.returnStatus);
            } else {
                showError(result.payload);
            }
        },
        [dispatch, openReturnSlip, showError],
    );

    const openPackage = useCallback(
        async (match: BatchPackageMatch) => {
            if (match.existingReturnId != null) {
                openReturnSlip(match.existingReturnId, match.existingReturnStatus);
                return;
            }

            if (match.batchStatus === BatchReturnStatusType.Completed) {
                // A package marked missing turned up after all: the receiver may reopen the batch for it.
                if (match.state === BatchPackageState.Missing && match.companyHasReceived) {
                    setPrompt({ kind: 'reopen', match });
                    return;
                }
                toast.info(
                    t(
                        'batchPackage.belongsToCompletedBatch',
                        'This package belongs to batch #{{batchId}}, which is already completed.',
                        {
                            batchId: match.batchReturnId,
                        },
                    ),
                    toastOptions,
                );
                return;
            }

            if (match.state === BatchPackageState.Missing) {
                setPrompt({ kind: 'missing', match });
                return;
            }

            if (!match.companyHasReceived) {
                setPrompt({ kind: 'receive', match });
                return;
            }

            await createSlip(match);
        },
        [createSlip, openReturnSlip, t],
    );

    /** Handles a barcode result of type "batchPackage". */
    const handlePackages = useCallback(
        async (matches: BatchPackageMatch[]) => {
            if (matches.length > 1) {
                setPrompt({ kind: 'choose', matches });
            } else if (matches.length === 1) {
                await openPackage(matches[0]);
            }
        },
        [openPackage],
    );

    const choose = useCallback(
        async (match: BatchPackageMatch) => {
            setPrompt(null);
            await openPackage(match);
        },
        [openPackage],
    );

    /** Confirms the open receive, missing or reopen prompt, then creates the slip. */
    const confirm = useCallback(
        async (reopenComment?: string) => {
            if (!prompt || prompt.kind === 'choose') return;

            const { match } = prompt;
            const batchReturnId = match.batchReturnId!;
            const sourceReturnId = match.sourceReturnId!;
            setBusy(true);
            try {
                // Reopening also takes the package back, in one step: never reopened but still missing.
                const result = await (prompt.kind === 'receive'
                    ? dispatch(receiveBatchReturn(batchReturnId))
                    : prompt.kind === 'reopen'
                    ? dispatch(reopenBatch({ batchReturnId, comment: reopenComment ?? '', sourceReturnId }))
                    : dispatch(unmarkPackageMissing({ batchReturnId, sourceReturnId })));
                if ('error' in result) {
                    const error = result.payload as BatchPackageActionError | undefined;
                    if (prompt.kind === 'missing') showError(error);
                    else
                        toast.error(
                            error?.message ?? t('batchReturn.actionFailed', 'Action failed. Please try again.'),
                            toastOptions,
                        );
                    return;
                }
                await createSlip(match);
            } finally {
                setBusy(false);
                setPrompt(null);
            }
        },
        [createSlip, dispatch, prompt, showError, t],
    );

    /** Declining creates nothing: the scan stops here. */
    const cancel = useCallback(() => setPrompt(null), []);

    return { prompt, busy, handlePackages, choose, confirm, cancel };
};

export default useBatchPackageScan;
